import { useRef, useState, useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';

import { CommentForm } from './CommentForm';
import { Replies } from './Replies';
import { SimpleComment } from './SimpleComment';

const CommentWithReplies = ({
  canDisplayEmojiPicker,
  comment: mainComment,
  currentUser,
  options,
  isOpenInModal,
  onDeleteComment,
  onToggleLike,
  onUpdateComment,
  onVote,
  parentUrl,
}) => {
  const [displayCommentInput, setDisplayCommentInput] = useState(false);
  const [externalMention, setExternalMention] = useState();

  const quillRef = useRef();

  const handleOnReply = (mentionProps) => {
    // handle on reply clicked
    if (!displayCommentInput) {
      setDisplayCommentInput(true);
    } else {
      focusCommentInput();
    }

    if (mentionProps) {
      setExternalMention(mentionProps);
    }
  };

  const focusCommentInput = () => {
    setTimeout(() => quillRef?.current?.focus(), 50);
  };

  const handleCreateReply = useCallback(
    ({ newComment: reply }) => {
      const totalReplies = mainComment.replies.total;
      const currentItems = mainComment?.replies?.items || [];
      const newItems = [...currentItems, reply];

      onUpdateComment({
        comment: {
          ...mainComment,
          replies: { total: totalReplies + 1, items: newItems },
        },
      });
    },
    [onUpdateComment, mainComment],
  );

  const handleUpdateReply = useCallback(
    ({ comment: reply }) => {
      const totalReplies = mainComment.replies.total;
      const replies = mainComment.replies?.items?.map((obj) =>
        obj.id === reply.id ? reply : obj,
      );

      onUpdateComment({
        comment: {
          ...mainComment,
          replies: { total: totalReplies, items: replies },
        },
      });
    },
    [onUpdateComment, mainComment],
  );

  const handleDeleteReply = useCallback(
    ({ id }) => {
      const totalReplies = mainComment.replies.total;
      const replies = mainComment?.replies?.items?.filter(
        (element) => element.id !== id,
      );

      onUpdateComment({
        comment: {
          ...mainComment,
          replies: { total: totalReplies - 1, items: replies },
        },
      });
    },
    [onUpdateComment, mainComment],
  );

  const handleToggleLikeReply = useCallback(
    ({ newLikes, commentId }) => {
      const index = mainComment.replies?.items?.findIndex(
        (element) => element.id === commentId,
      );

      if (index >= 0) {
        const newItems = [...mainComment.replies.items];
        const element = newItems[index];

        const likesToCorrectComment = {
          ...element,
          likes: newLikes,
        };

        newItems[index] = likesToCorrectComment;

        onUpdateComment({
          comment: {
            ...mainComment,
            replies: {
              total: mainComment.replies.items.length,
              items: newItems,
            },
          },
        });
      }
    },
    [onUpdateComment, mainComment],
  );

  const handleUpdateReplies = useCallback(
    ({ replies, totalReplies }) => {
      onUpdateComment({
        comment: {
          ...mainComment,
          replies: { total: totalReplies, items: replies },
        },
      });
    },
    [onUpdateComment, mainComment],
  );

  return (
    <Box flexDirection="column" width="100%">
      <Box pb={4}>
        <SimpleComment
          canDisplayEmojiPicker={canDisplayEmojiPicker}
          comment={mainComment}
          currentUser={currentUser}
          options={options}
          onDeleteComment={onDeleteComment}
          onReply={handleOnReply}
          onToggleLike={onToggleLike}
          onUpdateComment={onUpdateComment}
          onVote={onVote}
          parentUrl={parentUrl}
        />
      </Box>
      {mainComment?.replies?.total > 0 && (
        <Box pl={10} pb={3} flexDirection="column">
          <Replies
            canDisplayEmojiPicker={canDisplayEmojiPicker}
            commentableId={mainComment.id}
            commentableType="Comment"
            currentUser={currentUser}
            isOpenInModal={isOpenInModal}
            onDeleteReply={handleDeleteReply}
            onReply={handleOnReply}
            onToggleLike={handleToggleLikeReply}
            onUpdateReplies={handleUpdateReplies}
            onUpdateReply={handleUpdateReply}
            parentUrl={parentUrl}
            replies={mainComment.replies}
          />
        </Box>
      )}
      {displayCommentInput && (
        <Box pl={17}>
          <CommentForm
            autoFocus
            canDisplayEmojiPicker={canDisplayEmojiPicker}
            commentableId={mainComment.id}
            commentableType="Comment"
            commentId={mainComment.id}
            externalMention={externalMention}
            handleOnFocus={focusCommentInput}
            isReply
            onComment={handleCreateReply}
            ref={quillRef}
            user={currentUser}
          />
        </Box>
      )}
    </Box>
  );
};

export { CommentWithReplies };
